import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalIcon, Plus } from "lucide-react";
import BulkActionsBar from "@/components/BulkActionsBar";
import { BOOKING_STATUSES, STATUS_BADGE_CLASS } from "@/lib/constants";
import BookingsFilters from "./bookings/BookingsFilters";
import BookingsTable from "./bookings/BookingsTable";
import BookingsDialogs from "./bookings/BookingsDialogs";
import { useBookingsData, useSelection } from "./bookings/useBookingsData";
import { useBookingActions } from "./bookings/useBookingActions";

function EmptyState({ onAdd }) {
  return (
    <div className="py-12 text-center" data-testid="bookings-empty-state">
      <div className="mx-auto h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
        <CalIcon className="h-6 w-6 text-slate-400" />
      </div>
      <h3 className="text-sm font-medium">No bookings match these filters</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
        Adjust the filters above, or create a new booking to start populating the dock schedule.
      </p>
      <Button className="mt-4" onClick={onAdd} data-testid="empty-add-booking-btn">
        <Plus className="h-4 w-4 mr-1.5" /> New booking
      </Button>
    </div>
  );
}

function StatusSummary({ rows }) {
  const counts = useMemo(() => {
    const c = {};
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);
  if (!rows.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="bookings-status-summary">
      {BOOKING_STATUSES.filter((s) => counts[s.value]).map((s) => (
        <Badge key={s.value} variant="secondary" className={STATUS_BADGE_CLASS[s.tone]}>
          {s.label}: {counts[s.value]}
        </Badge>
      ))}
    </div>
  );
}

export default function Bookings() {
  const data = useBookingsData();
  const { rows, loading, refresh } = data;
  const selection = useSelection(rows);
  const actions = useBookingActions({ refresh, selection });
  const addNew = () => actions.setEditing({ __new: true });
  const bulkActions = [
    { key: "approve",  label: "Approve",  onClick: () => actions.bulkTransition("approved") },
    { key: "reject",   label: "Reject",   onClick: () => actions.bulkTransition("rejected"), variant: "outline" },
    { key: "cancel",   label: "Cancel",   onClick: () => actions.bulkTransition("cancelled"), variant: "outline" },
    { key: "complete", label: "Complete", onClick: () => actions.bulkTransition("completed"), variant: "outline" },
    { key: "delete",   label: "Delete",   onClick: actions.bulkDelete, variant: "destructive" },
  ];

  return (
    <div data-testid="bookings-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Bookings</h1>
          <p className="text-sm text-slate-500">Inbound and outbound dock slots across all sites you can see.</p>
        </div>
        <Button onClick={addNew} data-testid="add-booking-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New booking
        </Button>
      </div>
      <BookingsFilters filters={data.filters} setFilter={data.setFilter} sites={data.sites} />
      <StatusSummary rows={rows} />
      <BulkActionsBar selectedCount={selection.selectedIds.size} onClear={selection.clear} actions={bulkActions} />
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">
            {loading ? "Loading…" : `${rows.length} booking${rows.length === 1 ? "" : "s"}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {rows.length === 0 && !loading
            ? <EmptyState onAdd={addNew} />
            : <BookingsTable rows={rows} selection={selection} lifecycle={data.lifecycle} actions={actions} />}
        </CardContent>
      </Card>
      <BookingsDialogs actions={actions} refresh={refresh} />
    </div>
  );
}
